import _ from 'lodash';
import axios from 'axios';
import awsHeader from '../helpers/awsAuth';
import lambdaBaseApi from '../apis/lambda';
import moment from 'moment';
import template from 'url-template';
import { ConcurrencyManager } from 'axios-concurrency';

import { FETCH_RULE_KPIS, FETCH_RULES, RULES_DELETE, RULES_UPDATE, FETCH_RULE_LIST, SET_RULE_START_TIMESTAMP, SET_RULE_END_TIMESTAMP, FETCH_RULE_TARGETS, SET_ACTIVE_RULE, SET_LEGACY_RULE_TABLE_PAGE_SIZE } from '../actions/actionTypes';
import { handleNetworkError } from './applicationMessageActions';
import { prepForSave } from '../helpers/rules';
import { syncSensorsXml } from '../helpers/sensors';
import { setBobLoading } from './bobActions';

const RuleKPIUrl = template.parse('/rules/kpis');
const RulesUrl = template.parse('/rules{/id}{?name,orderby,page,pageSize}');
const RuleListUrl = template.parse('rules/rules-find{?name,orderby,page,pageSize,isCountQuery}');
const Rule = template.parse('/rules/{ruleId}');
const RuleExceptionsUrl = template.parse('/rules/{ruleId}/executionlogs{?clientId,siteId,entityId,endTimestamp,page,pageSize,query,startTimestamp}');
const RuleTargetsUrl = template.parse('/rules/{ruleId}/targets{?searchFilter,page,pageSize}');

const DEFAULT_PAGE_LIMIT = 20;
const DEFAULT_SORT = 'name asc';
const MAX_CONCURRENCY_REQUESTS = 10;

/*
TODO: refactor the remaining actions to use handleNetworkError for error handling where appropriate (SB)
 */

export const deleteRule = (rule, pageName='') => async (dispatch) => {
    const headers = await awsHeader();
    if (pageName !== 'BobRuleBuilder') {
        dispatch(setBobLoading('deleteRule'));
    }
    
    // eslint-disable-next-line no-useless-catch
    try {
        await lambdaBaseApi({
            ...headers,
            method: 'DELETE',
            url:    RulesUrl.expand(rule)
        });

        // This dispatch is deferred because removing the item from Redux state
        // will cause a re-render likely resulting in the removal of a DOM
        // node. This can cause an issue when a mounted component is in the
        // middle of an async task because when the task completes, the
        // component is no longer mounted which prevents it from the ability to
        // perform any after effects. This delay allows React to re-render
        // in response to the async operation before unmounting the component.
        requestAnimationFrame(() => {
            dispatch({
                type:    RULES_DELETE,
                payload: rule
            });
        });
    // eslint-disable-next-line no-useless-catch
    } catch (error) {
        throw error;
    } finally {
        if (pageName !== 'BobRuleBuilder') {
            dispatch(setBobLoading('deleteRule'));
        }
    }
};

export const fetchRuleKPIs = () => async (dispatch) => {
    const headers = await awsHeader();
    
    dispatch(setBobLoading('fetchRuleKPIs'));

    try {
        const response = await lambdaBaseApi.get(
            RuleKPIUrl.expand({}), headers
        );
        const payload = _.get(response, 'data.data', {});
        dispatch({
            type: FETCH_RULE_KPIS,
            payload
        });
        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch({
            type:    FETCH_RULE_KPIS,
            payload: {
                data: {}
            }
        });
        return Promise.reject(error);
    } finally {
        dispatch(setBobLoading('fetchRuleKPIs'));
    }
};

export const fetchRules = (
    {
        orderby = 'name asc',
        page,
        ...params
    } = {}
) => async (dispatch) => {
    const headers = await awsHeader();
    if (page) {
        /**
         * Most pagination schemes use 0-based numbering (including the
         * DevExtreme pagination plugin), but the backend is 1-based so we
         * need to adjust the parameter accordingly so that the correct page is
         * requested.
         */
        page = parseInt(page, 10) + 1;
    }
    try {
        const body = {
            clients:    [],
            sites:      [],
            equipments: []
        };

        const countresponse = await lambdaBaseApi.post(
            RuleListUrl.expand({
                ...params,
                isCountQuery: true
            }),
            body,
            headers
        );
        const totalCount = countresponse.data.meta.totalCount;

        const response = await lambdaBaseApi.post(
            RuleListUrl.expand({
                ...params,
                page,
                orderby
            }),
            body,
            headers
        );
        const payload = response.data;
        payload.meta.totalCount = totalCount;

        payload.data.forEach(rule => {
            rule.formulaXml = rule.formulaXml.replaceAll('deletable="false" movable="false" editable="false"', '');
            rule.faultLogicXml = rule.faultLogicXml.replaceAll('deletable="false" movable="false" editable="false"', '');
        });
        dispatch({
            type: FETCH_RULES,
            payload
        });
        return payload;
    } catch (error) {
        // If the fetch failed, clear the state so that any collection views
        // do not rendering out of sync state.
        dispatch({
            type:    FETCH_RULES,
            payload: {
                data: []
            }
        });
        throw error;
    }
};

export const getRule = (id) => async (dispatch) => {
    const headers = await awsHeader();
    dispatch(setBobLoading('enableOrDisableOrDuplicateRuleFromRuleBuilderPage'));

    // eslint-disable-next-line no-useless-catch
    try {
        const response = await lambdaBaseApi.get(
            RulesUrl.expand({ id }),
            headers
        );
        const payload = resyncWorkspaces(response.data);
        payload.data.formulaXml = payload.data.formulaXml.replaceAll('deletable="false" movable="false" editable="false"', '');
        payload.data.faultLogicXml = payload.data.faultLogicXml.replaceAll('deletable="false" movable="false" editable="false"', '');
        dispatch({
            type: RULES_UPDATE,
            payload
        });
        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        throw error;
    } finally {
        dispatch(setBobLoading('enableOrDisableOrDuplicateRuleFromRuleBuilderPage'));
    }
};

export const recalculateRule = ({ end, start, ...recalculation }) => async (dispatch) => {
    const headers = await awsHeader();

    const newEnd = end === start ? moment.utc(end, 'YYYY-MM-DD').add(1,'d').subtract(1, 's').toISOString() : moment.utc(end, 'YYYY-MM-DD').toISOString();

    // eslint-disable-next-line no-useless-catch
    try {
        const response = await lambdaBaseApi({
            data: {
                ...recalculation,
                end:   newEnd,
                start: moment.utc(start, 'YYYY-MM-DD').toISOString()
            },
            ...headers,
            method: 'POST',
            url:    '/rules/recalculation'
        });
        // Since `/recalculate` does not return a response, we need to refresh
        // the state of the rule so that the UI can reflect the pending
        // recalculation state.
        await dispatch(getRule(recalculation.id));
        return response;
    } catch (error) {
        throw error;
    }
};

export const saveRule = (rule, pageName='') => async (dispatch) => {
    const headers = await awsHeader();

    if (pageName !== 'BobRuleBuilder') {
        dispatch(setBobLoading('enableOrDisableOrDuplicateRuleFromRuleBuilderPage'));
    }

    // eslint-disable-next-line no-useless-catch
    try {
        rule.formulaXml = rule.formulaXml.replaceAll('deletable="false" movable="false" editable="false"', '');
        rule.faultLogicXml = rule.faultLogicXml.replaceAll('deletable="false" movable="false" editable="false"', '');
        const response = await lambdaBaseApi({
            data:   prepForSave(rule),
            ...headers,
            method: rule.id ? 'PUT' : 'POST',
            url:    RulesUrl.expand({})
        });
        const payload = resyncWorkspaces(response.data || rule);

        dispatch({
            type: RULES_UPDATE,
            payload
        });
        return payload;
    // eslint-disable-next-line no-useless-catch
    } catch (error) {
        if (pageName !== 'BobRuleBuilder') {
            dispatch(handleNetworkError(error));
        }
        throw error;
    } finally {
        if (pageName !== 'BobRuleBuilder') {
            dispatch(setBobLoading('enableOrDisableOrDuplicateRuleFromRuleBuilderPage'));
        }
    }
};

/**
 * Ensure workspace XML data is synchronized with the sensor data. This
 * is primarily needed when a new sensor with a temporary client-side ID
 * has been persisted to the backend gaining a real GUID in the process.
 *
 * The XML data needs to be resyncrhonized with the new sensor data so
 * that the new sensors are available for selection by the user.
 */
function resyncWorkspaces(payload) {
    payload.data.faultLogicXml = syncSensorsXml(
        payload.data.sensors,
        payload.data.faultLogicXml
    );
    payload.data.formulaXml = syncSensorsXml(
        payload.data.sensors,
        payload.data.formulaXml
    );

    payload.data.sensors.forEach(sensor => sensor.label = sensor.sensorTypeName);

    return payload;
}


// Below are used in Dashboard
export const fetchRuleDatails = ruleId => async (dispatch) => {
    const header = await awsHeader();
    
    dispatch(setBobLoading('fetchRuleInfo'));

    try {
        const response = await lambdaBaseApi.get(
            Rule.expand({
                ruleId
            }),
            header
        );

        const data = _.get(response, 'data.data', {});
        return data;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchRuleInfo'));
    }
};

export const setRuleList = (data = [], meta = {}) => async (dispatch) => {
    dispatch({
        type:    FETCH_RULE_LIST,
        payload: {
            data,
            meta
        }
    });
};

export const fetchRuleList = (companyId, body, options = {}, sort) => async (dispatch) => {
    const header = await awsHeader();
    
    header.headers['K2A-Company'] = companyId;

    options.pageSize || (options.pageSize = 20);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.orderby = sort || (options.orderby = DEFAULT_SORT);
    options.id = companyId;

    dispatch(setBobLoading('fetchRuleList'));

    try {
        const manager = ConcurrencyManager(lambdaBaseApi, MAX_CONCURRENCY_REQUESTS);
        options.isCountQuery = true;
        const response = await lambdaBaseApi.post(
            RuleListUrl.expand({
                ...options
            }),
            body,
            header
        );

        const data = _.get(response, 'data.data', []) || [];
        const meta = _.get(response, 'data.meta', {});
        const total = meta.totalCount;

        dispatch({
            type:    FETCH_RULE_LIST,
            payload: {
                data,
                meta
            }
        });

        let totalPages = total / options.pageSize;
        let count;
        if (Number.isInteger(totalPages)) {
            count = totalPages;
        } else {
            count = totalPages + 1;
        }

        // fetching all rules as a background process
        let promises = [];
        for (let i = 1; i <= count; i++) {
            options.page = i;
            options.isCountQuery = false;
            promises.push(
                lambdaBaseApi.post(
                    RuleListUrl.expand({
                        ...options
                    }),
                    body,
                    header
                )
            );
        }

        let results = [];

        await Promise.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        manager.detach();

        let allRules = data.concat(...results);

        dispatch({
            type:    FETCH_RULE_LIST,
            payload: {
                data: allRules,
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
    finally {
        dispatch(setBobLoading('fetchRuleList'));
    }
};

export const fetchRuleDebuggerList= (ruleId, options = {}) => async (dispatch) => {
    const header = await awsHeader();
    
    dispatch(setBobLoading('fetchRuleDebuggerList'));

    options.pageSize || (options.pageSize = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);

    try {
        const response = await lambdaBaseApi.get(
            RuleExceptionsUrl.expand({
                ruleId,
                ...options
            }),
            header
        );

        const data = _.get(response, 'data.data', {});
        const meta = _.get(response, 'data.meta', {});

        return { data, meta };
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchRuleDebuggerList'));
    }
};

export const fetchRuleTargets= (ruleId, options = {}) => async (dispatch) => {
    const header = await awsHeader();
    
    dispatch({
        type:    FETCH_RULE_TARGETS,
        payload: {
            data: [],
            mets: {}
        }
    });

    dispatch(setBobLoading('fetchRuleTargets'));

    options.pageSize || (options.pageSize = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);

    try {
        const response = await lambdaBaseApi.get(
            RuleTargetsUrl.expand({
                ruleId,
                ...options
            }),
            header
        );

        const data = _.get(response, 'data.data', {});
        const meta = _.get(response, 'data.meta', {});


        dispatch({
            type:    FETCH_RULE_TARGETS,
            payload: {
                data,
                meta
            }
        });
        
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchRuleTargets'));
    }
};

export const setRuleStartTimestamp = timestamp => async dispatch => {
    dispatch({
        type:    SET_RULE_START_TIMESTAMP,
        payload: timestamp
    });
};

export const setRuleEndTimestamp = timestamp => async dispatch => {
    dispatch({
        type:    SET_RULE_END_TIMESTAMP,
        payload: timestamp
    });
};

export const loading = () => async (dispatch) => {
    dispatch(setBobLoading('actionMenuClickFromRuleBuilderPage'));
};

export const setActiveRule = rule => async dispatch => {
    dispatch({
        type:    SET_ACTIVE_RULE,
        payload: rule
    });
};

export const setLegacyRuleTablePageSize = pageSize => async dispatch => {
    dispatch({
        type:    SET_LEGACY_RULE_TABLE_PAGE_SIZE,
        payload: pageSize
    });
};
